import { useCallback, useEffect, useRef, useState } from "react"
import { MySelector } from "../../redux/store"
import PhotoCard from "./PhotoCard"
import { scrollToGalleryTop } from "../../utils/scrollToGalleryTop"

interface Photos {
  id: string
  name: string
  url: string
  compressedUrl?: string
  b2Url?: string
  b2CompressedUrl?: string
}

interface LayoutWrapperProps {
  overridePhotos?: Photos[]
}

const BATCH_SIZE = 90

const LayoutWrapper = ({ overridePhotos }: LayoutWrapperProps) => {
  const { currentFolderData, type } = MySelector((state) => state.util)
  const { activeAlbumFiles } = MySelector((state) => state.gallery)

  const photos: Photos[] = overridePhotos || (type === "gallery" ? currentFolderData : activeAlbumFiles || [])
  const prevPhotosLength = useRef<number>(0)

  const [loadedCount, setLoadedCount] = useState(BATCH_SIZE)

  const observerRef = useRef<HTMLDivElement | null>(null)

  const loadMorePhotos = useCallback(() => {
    setLoadedCount((prev) => Math.min(prev + BATCH_SIZE, photos?.length))
  }, [photos?.length])

  const splitPhotos = (size: number) => {
    if (photos?.length === 0) return []

    return photos
      ?.slice(0, loadedCount)
      ?.reduce<Photos[][]>(
        (acc, _, i) =>
          i % size === 0 ? [...acc, photos?.slice(i, i + size)] : acc,
        []
      )
  }

  // to trigger loading more photos
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          loadMorePhotos()
        }
      },
      { threshold: 0.8 }
    )

    const target = observerRef.current

    if (target) observer.observe(target)

    return () => {
      if (target) observer.unobserve(target)
    }
  }, [loadMorePhotos])

  // On a folder/tab change or AI results, jump to the folder bar (not the page
  // top / cover). Skip the first load so the cover stays in view, and skip when
  // photos were merely removed.
  useEffect(() => {
    const currentLength = photos?.length || 0
    const previousLength = prevPhotosLength.current

    if (previousLength !== 0 && currentLength >= previousLength) {
      scrollToGalleryTop()
    }

    prevPhotosLength.current = currentLength
  }, [photos])

  return (
    <div>
      {/* Mobile Layout */}
      <div className="mx-2 space-y-2 lg:hidden">
        {splitPhotos(4)?.map((sp, index) => (
          <div
            key={index}
            className="grid grid-cols-2 h-[45vh] md:h-[82vh] gap-1"
          >
            {Array?.from({ length: 2 })?.map((_, colIndex) => (
              <div key={colIndex} className="h-[45vh] md:h-[82vh] space-y-1">
                {[0, 1]?.map((rowIndex) => {
                  const itemIndex = colIndex * 2 + rowIndex
                  const item = sp[itemIndex]
                  const ratios = ["h-2/3", "h-1/3", "h-1/3", "h-2/3"]

                  return (
                    item && (
                      <PhotoCard
                        key={item?.id}
                        ratio={ratios[itemIndex]}
                        item={item}
                      />
                    )
                  )
                })}
              </div>
            ))}
          </div>
        ))}
      </div>

      {/* Desktop Layout */}
      <div className="mx-2 space-y-3 hidden lg:block">
        {splitPhotos(6)?.map((sp, index) => (
          <div key={index} className="grid grid-cols-3 h-screen gap-1.5">
            {Array?.from({ length: 3 })?.map((_, colIndex) => (
              <div key={colIndex} className="h-screen space-y-1.5">
                {[0, 1]?.map((rowIndex) => {
                  const itemIndex = colIndex * 2 + rowIndex
                  const item = sp[itemIndex]
                  const ratios = [
                    "h-2/3",
                    "h-1/3",
                    "h-1/3",
                    "h-2/3",
                    "h-2/3",
                    "h-1/3",
                  ]

                  return (
                    item && (
                      <PhotoCard
                        key={item?.id}
                        ratio={ratios[itemIndex]}
                        item={item}
                      />
                    )
                  )
                })}
              </div>
            ))}
          </div>
        ))}
      </div>

      {/* Intersection Observer Target */}
      <div ref={observerRef} className="h-10 w-full"></div>
    </div>
  )
}

export default LayoutWrapper
